import { Suspense, lazy } from "react";
import type { RouteObject } from "react-router-dom";
import { Navigate } from "react-router-dom";
import AppShell from "../components/layout/app-shell";
import NotFound from "../pages/not-found";
import Home from "../pages/home/page";
import RequireAuth from "./require-auth";
import RedirectLegacyPagesRoute from "./redirect-legacy-pages-route";
import RedirectLegacyTagsRoute from "./redirect-legacy-tags-route";

// ── Marketing pages (lazy) ──────────────────────────
const Features = lazy(() => import("../pages/features/page"));
const Pricing = lazy(() => import("../pages/pricing/page"));
const Cases = lazy(() => import("../pages/cases/page"));
const Help = lazy(() => import("../pages/help/page"));
const Terms = lazy(() => import("../pages/legal/terms"));
const Privacy = lazy(() => import("../pages/legal/privacy"));
const Company = lazy(() => import("../pages/legal/company"));

// ── Auth pages (lazy) ───────────────────────────────
const SignIn = lazy(() => import("../pages/auth/sign-in"));
const SignUp = lazy(() => import("../pages/auth/sign-up"));
const AuthCallback = lazy(() => import("../pages/auth/callback"));

// ── App pages (lazy) ────────────────────────────────
const AppDashboard = lazy(() => import("../pages/app/dashboard"));
const AppEditor = lazy(() => import("../pages/app/editor"));
const AppPublish = lazy(() => import("../pages/app/publish"));
const AppWizard = lazy(() => import("../pages/app/wizard"));
const AppTheme = lazy(() => import("../pages/app/theme"));
const AppForms = lazy(() => import("../pages/app/forms"));
const AppAssets = lazy(() => import("../pages/app/assets"));
const AppPrecheck = lazy(() => import("../pages/app/precheck"));
const AppAnalytics = lazy(() => import("../pages/app/analytics"));
const AppBilling = lazy(() => import("../pages/app/billing"));
const AppTemplateCatalog = lazy(() => import("../pages/app/template-catalog"));
const AppTemplates = lazy(() => import("../pages/app/templates"));

// ── Published site (lazy) ───────────────────────────
const PublishedSite = lazy(() => import("../pages/published/page"));
const PreviewShared = lazy(() => import("../pages/published/preview-shared"));
const PreviewLocal = lazy(() => import("../pages/published/preview-local"));
const PreviewWorkspaceLocal = lazy(() => import("../pages/published/preview-workspace-local"));

// ── Loading fallback ────────────────────────────────
const pageLoadingFallback = (
  <div className="min-h-screen flex items-center justify-center">
    <p className="text-sm text-textMuted">読み込み中...</p>
  </div>
);

const appPageLoadingFallback = (
  <section className="w-full app-card p-8">
    <p className="text-sm text-textMuted">読み込み中...</p>
  </section>
);

function withPageLoading(children: React.ReactNode) {
  return <Suspense fallback={pageLoadingFallback}>{children}</Suspense>;
}

function withAppPageLoading(children: React.ReactNode) {
  return <Suspense fallback={appPageLoadingFallback}>{children}</Suspense>;
}

const routes: RouteObject[] = [
  {
    path: "/",
    element: <Home />,
  },
  {
    path: "/features",
    element: withPageLoading(<Features />),
  },
  {
    path: "/pricing",
    element: withPageLoading(<Pricing />),
  },
  {
    path: "/cases",
    element: withPageLoading(<Cases />),
  },
  {
    path: "/help",
    element: withPageLoading(<Help />),
  },
  {
    path: "/terms",
    element: withPageLoading(<Terms />),
  },
  {
    path: "/privacy",
    element: withPageLoading(<Privacy />),
  },
  {
    path: "/company",
    element: withPageLoading(<Company />),
  },
  {
    path: "/auth/sign-in",
    element: withPageLoading(<SignIn />),
  },
  {
    path: "/auth/sign-up",
    element: withPageLoading(<SignUp />),
  },
  {
    path: "/auth/callback",
    element: withPageLoading(<AuthCallback />),
  },
  {
    path: "/login",
    element: <Navigate to="/auth/sign-in" replace />,
  },
  {
    path: "/try",
    element: <RequireAuth />,
    children: [
      {
        index: true,
        element: <Navigate to="/app/wizard" replace />,
      },
    ],
  },
  {
    path: "/preview/:token",
    element: withPageLoading(<PreviewShared />),
  },
  {
    path: "/preview/local/:previewId",
    element: withPageLoading(<PreviewLocal />),
  },
  {
    path: "/preview/workspace/local/:previewId",
    element: withPageLoading(<PreviewWorkspaceLocal />),
  },
  {
    path: "/sites/:slug/*",
    element: withPageLoading(<PublishedSite />),
  },
  {
    path: "/app",
    element: <RequireAuth />,
    children: [
      {
        element: <AppShell />,
        children: [
          {
            index: true,
            element: withAppPageLoading(<AppDashboard />),
          },
          {
            path: "wizard",
            element: withAppPageLoading(<AppWizard />),
          },
          {
            path: "precheck",
            element: withAppPageLoading(<AppPrecheck />),
          },
          {
            path: "editor/:siteId",
            element: withAppPageLoading(<AppEditor />),
          },
          {
            path: "pages/:siteId",
            element: <RedirectLegacyPagesRoute />,
          },
          {
            path: "theme/:siteId",
            element: withAppPageLoading(<AppTheme />),
          },
          {
            path: "assets/:siteId",
            element: withAppPageLoading(<AppAssets />),
          },
          {
            path: "tags/:siteId",
            element: <RedirectLegacyTagsRoute />,
          },
          {
            path: "forms/:siteId",
            element: withAppPageLoading(<AppForms />),
          },
          {
            path: "publish/:siteId",
            element: withAppPageLoading(<AppPublish />),
          },
          {
            path: "analytics/:siteId",
            element: withAppPageLoading(<AppAnalytics />),
          },
          {
            path: "billing",
            element: withAppPageLoading(<AppBilling />),
          },
          {
            path: "template-catalog",
            element: withAppPageLoading(<AppTemplateCatalog />),
          },
          {
            path: "templates",
            element: withAppPageLoading(<AppTemplates />),
          },
        ],
      },
    ],
  },
  {
    path: "*",
    element: <NotFound />,
  },
];

export default routes;
